import { useNavigate } from "react-router-dom";
import { ItemsList } from "../../../../common/components/itemsList/ItemsList";
import { Icon, IconType } from "../../../../common/icons/Icon";
import { useAddAdditionalLayoutButton } from "../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import {
  constructStudioHref,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import { IStudioModel } from "./models/studioModel";
import styles from "./StudiosList.module.scss";
import { classNames } from "primereact/utils";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useAppContext } from "../../../../AppContext";
import { UserRole } from "../../../../common/enums";
import { getCurrentUserRole } from "../../../../common/infrastructure/helpers/userHelper";
import { useMemo } from "react";

export const StudiosList = () => {
  const { currentUser } = useAppContext();

  const currentUserRole = useMemo(
    () => getCurrentUserRole(currentUser),
    [currentUser],
  );

  // Add tenant information request
  useAddAdditionalLayoutButton({
    iconType: IconType.PlusIcon,
    href: routePaths.studioAdd,
    validator: currentUserRole === UserRole.GlobalAdmin,
  });

  const { data: studiosResult, isLoading } = useApiQuery<IStudioModel[]>({
    queryKey: [endpointsMetadata.studio.getAll],
    endpoint: endpointsMetadata.studio.getAll,
    globalIsLoading: false,
  });

  return (
    <ItemsList
      isLoading={isLoading}
      className={styles.root}
      title="Всички обекти"
      noItemsText="Няма намерени обекти."
      itemTemplate={(item, index, actionButtons, className) => {
        return (
          <StudioItem
            className={className}
            isLoading={isLoading}
            item={item}
            key={item?.id ?? index}
            actionButtons={actionButtons}
          />
        );
      }}
      items={studiosResult ?? []}
      swipeLeftActions={[
        {
          enablePrompt: true,
          promptMessage: "Сигурни ли сте, че искате да изтриете този обект?",
          method: "delete",
          iconType: IconType.DeleteIcon,
          endpoint: endpointsMetadata.studio.deleteStudio,
          validator: (item: IStudioModel) => !item.isCancelled,
          invalidateKeys: [
            [endpointsMetadata.studio.getAll],
            [endpointsMetadata.initialization.getUserStudios],
          ],
        },
        {
          enablePrompt: true,
          promptMessage:
            "Сигурни ли сте, че искате да възстановите този обект?",
          method: "put",
          iconType: IconType.PutBackIcon,
          endpoint: endpointsMetadata.studio.putBackStudio,
          validator: (item: IStudioModel) => item.isCancelled,
          invalidateKeys: [
            [endpointsMetadata.studio.getAll],
            [endpointsMetadata.initialization.getUserStudios],
          ],
        },
      ]}
      swipeRightActions={[
        {
          type: "edit",
          endpoint: routePaths.studioEdit,
          iconType: IconType.EditIcon,
          validator: (item: IStudioModel) => !item.isCancelled,
        },
      ]}
      deletedFilter={{
        storageKey: "studios",
        isDeleted: (item: IStudioModel) => !!item.isCancelled,
        sectionTitle: "Изтрити обекти",
        toggleLabel: "Покажи изтритите обекти",
      }}
      showItemsCount
      enableSearch
      fullHeight
    />
  );
};

interface IStudioItemProps {
  className?: string;
  item: IStudioModel | undefined;
  isLoading: boolean;
  actionButtons?: React.ReactNode;
}

export const StudioItem = ({
  className,
  item,
  isLoading,
  actionButtons,
}: IStudioItemProps) => {
  const navigate = useNavigate();

  const onClick = () => {
    if (item) {
      const href = constructStudioHref(item?.id);
      navigate(href);
    }
  };

  return (
    <div
      className={classNames(className, styles.item, {
        [styles.item_cancelled]: item?.isCancelled,
      })}
      onClick={onClick}
    >
      <div className={styles.item_left}>
        <SkeletonItem
          isLoading={isLoading}
          width={150}
          value={
            <>
              <div className={styles.item_left_name}>
                {item?.name}
                <span className={styles.item_left_name_id}>
                  (ID: {item?.id})
                </span>
              </div>
            </>
          }
        />
      </div>

      {actionButtons}
      <Icon type={IconType.RightChevronIcon} />
    </div>
  );
};
